import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#0F172A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=1600"
          alt="Aviation"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0F172A]/70 noise-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-11 w-auto bg-white rounded-md px-3 py-2" />
            <span className="text-white font-display font-bold tracking-tight">PEREGRINE COMPANIES PORTAL</span>
          </div>
          <h1 className="text-white font-display font-extrabold text-5xl leading-[0.95] tracking-tight">
            Reset your<br /><span className="text-[#D4AF37]">access.</span>
          </h1>
          <div className="text-xs text-zinc-400 font-mono">
            6318 Union Avenue, Shreveport, LA 71106 · 318.222.2224
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Account Recovery</p>
          <h2 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Forgot password</h2>
          <p className="mt-2 text-zinc-500">Enter your email and we'll send you a link to reset your password.</p>

          {sent ? (
            <div className="mt-8 border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-5" data-testid="forgot-sent">
              <p className="text-sm text-[#0F172A]">If an account exists for <span className="font-medium">{email}</span>, we've emailed a password reset link. It expires in 1 hour and can be used once.</p>
              <p className="mt-2 text-xs text-zinc-500">Check your inbox (and spam folder). You can close this page.</p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="forgot-form">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                className="w-full px-4 py-3 border border-zinc-300 bg-white text-[#0A0A0A] font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                placeholder="you@company.com"
              />
            </div>

            {error && <p className="text-sm text-[#EF4444]" data-testid="forgot-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit"
              className="group w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Continue <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></>
              )}
            </button>
          </form>
          )}

          <Link
            to="/login"
            data-testid="back-to-login"
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#0F172A] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
